import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  createPageIntroductionRequest,
  getPagesIntroduction,
  removePageIntroduction,
  updatePageIntroductionRequest,
} from "../../../../services/api/landing/pagesIntroduction/pagesIntroductionApi";

const getLang = () =>
  localStorage.getItem("language") === "ar" ? "ar" : "en";

const toastMessages = {
  add: {
    en: "Page introduction created successfully!",
    ar: "تم إنشاء مقدمة الصفحة بنجاح!",
  },
  addError: {
    en: "Failed to create page introduction.",
    ar: "فشل في إنشاء مقدمة الصفحة.",
  },
  update: {
    en: "Page introduction updated successfully!",
    ar: "تم تحديث مقدمة الصفحة بنجاح!",
  },
  updateError: {
    en: "Failed to update page introduction.",
    ar: "فشل في تحديث مقدمة الصفحة.",
  },
  delete: {
    en: "Page introduction deleted successfully!",
    ar: "تم حذف مقدمة الصفحة بنجاح!",
  },
  deleteError: {
    en: "Failed to delete page introduction.",
    ar: "فشل في حذف مقدمة الصفحة.",
  },
};

export const fetchPagesIntroduction = createAsyncThunk(
  "pagesIntroduction/fetchPagesIntroduction",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPagesIntroduction();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch Pages Introduction");
    }
  },
);

export const createPageIntroduction = createAsyncThunk(
  "pagesIntroduction/createPageIntroduction",
  async (pageData, { rejectWithValue }) => {
    try {
      const response = await createPageIntroductionRequest(pageData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to create Page Introduction");
    }
  },
);

export const updatePageIntroduction = createAsyncThunk(
  "pagesIntroduction/updatePageIntroduction",
  async ({ pageKey, pageData }, { rejectWithValue }) => {
    try {
      const response = await updatePageIntroductionRequest(pageKey, pageData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to update Page Introduction");
    }
  },
);

export const deletePageIntroduction = createAsyncThunk(
  "pagesIntroduction/deletePageIntroduction",
  async (pageKey, { rejectWithValue }) => {
    try {
      await removePageIntroduction(pageKey);
      return pageKey;
    } catch (error) {
      return rejectWithValue("Failed to delete Page Introduction");
    }
  },
);

const pagesIntroductionSlice = createSlice({
  name: "pagesIntroduction",
  initialState: {
    pagesData: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPagesIntroduction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPagesIntroduction.fulfilled, (state, action) => {
        state.loading = false;
        state.pagesData = action.payload;
      })
      .addCase(fetchPagesIntroduction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createPageIntroduction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPageIntroduction.fulfilled, (state, action) => {
        state.loading = false;
        state.pagesData.push(action.payload);
        toast.success(toastMessages.add[getLang()]);
      })
      .addCase(createPageIntroduction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(toastMessages.addError[getLang()]);
      })
      .addCase(updatePageIntroduction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePageIntroduction.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.pagesData.findIndex(
          (page) => page.page_key === action.payload.page_key,
        );
        if (index !== -1) {
          state.pagesData[index] = action.payload;
        }
        toast.success(toastMessages.update[getLang()]);
      })
      .addCase(updatePageIntroduction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(toastMessages.updateError[getLang()]);
      })
      .addCase(deletePageIntroduction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePageIntroduction.fulfilled, (state, action) => {
        state.loading = false;
        state.pagesData = state.pagesData.filter(
          (page) => page.page_key !== action.payload,
        );
        toast.success(toastMessages.delete[getLang()]);
      })
      .addCase(deletePageIntroduction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(toastMessages.deleteError[getLang()]);
      });
  },
});

export default pagesIntroductionSlice.reducer;
